import React, { Component, ReactNode } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: any;
}

/**
 * Error boundary that catches rendering errors caused by invalid React children
 * (like objects being rendered directly in JSX)
 */
export class SafeRenderBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    // You can log the error here if needed
    console.error('SafeRenderBoundary caught an error:', error);
    console.error('Error Info:', errorInfo);

    // Check if it's the common "Objects are not valid as a React child" error
    if (error.message.includes('Objects are not valid as a React child')) {
      console.error('This error is likely caused by trying to render an object directly in JSX.');
      console.error('Make sure all data is properly converted to strings using safeRenderData()');
    }

    this.setState({
      error,
      errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      const { fallbackMessage = 'Something went wrong rendering this component.' } = this.props;

      return (
        <div className='rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-600 dark:bg-red-900/20'>
          <div className='flex'>
            <ExclamationTriangleIcon className='mt-0.5 h-5 w-5 text-red-400' />
            <div className='ml-3'>
              <h3 className='text-sm font-medium text-red-800 dark:text-red-200'>Rendering Error</h3>
              <div className='mt-2 text-sm text-red-700 dark:text-red-300'>
                <p>{fallbackMessage}</p>
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <details className='mt-2'>
                    <summary className='cursor-pointer font-medium'>Technical Details (Development Only)</summary>
                    <pre className='mt-2 whitespace-pre-wrap text-xs'>
                      {this.state.error.message}
                      {this.state.errorInfo?.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default SafeRenderBoundary;
